import { inject, injectable } from 'inversify'
import { unzip } from 'unzipit'

import { InjectionTokens } from '@/controller/tokens'

import { IObstacleRepository } from './IObstacleRepository'

import { describeHttpFailure } from '@/infra/http/httpFailure'
import type { IHttpClient } from '@/infra/http/IHttpClient'
import { HTTP_TIMEOUTS } from '@/infra/http/timeouts'
import type { ILogger } from '@/infra/logger/ILogger'

@injectable()
export class ObstacleRepository implements IObstacleRepository {
  // Cache Storage keeps the extracted doc.geojson blob clearable and separate from user data.
  private static readonly CACHE_NAME = 'planning-app-obstacles'
  private static readonly CACHE_URL = 'https://planning-app.local/cache/obstacles/doc.geojson'
  private static readonly CACHE_CREATED_AT_HEADER = 'x-cache-created-at'
  private static readonly CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000
  private static readonly DOC_GEOJSON_ENTRY = 'doc.geojson'

  private ready = false
  private activeUrls = new Set<string>()

  constructor(
    @inject(InjectionTokens.HttpClient) private readonly httpClient: IHttpClient,
    @inject(InjectionTokens.Logger) private readonly logger: ILogger
  ) {}

  async prepare(): Promise<string | null> {
    const cachedBlob = await this.getCachedBlob()

    if (cachedBlob) {
      return this.createUrl(cachedBlob)
    }

    this.logger.info('ObstacleRepository', 'Updating from remote')
    const remoteBlob = await this.fetchRemoteBlob()

    if (!remoteBlob) {
      this.ready = false
      return null
    }

    await this.cacheBlob(remoteBlob)
    this.logger.info('ObstacleRepository', 'Updated from remote')

    return this.createUrl(remoteBlob)
  }

  isReady(): boolean {
    return this.ready
  }

  release(url?: string): void {
    if (url) {
      this.revokeUrl(url)
      return
    }

    this.activeUrls.forEach((activeUrl) => URL.revokeObjectURL(activeUrl))
    this.activeUrls.clear()
    this.ready = false
  }

  async clearCache(): Promise<void> {
    if (!this.isCacheStorageAvailable()) {
      return
    }

    const cache = await caches.open(ObstacleRepository.CACHE_NAME)
    await cache.delete(ObstacleRepository.CACHE_URL)
  }

  protected getRemoteUrl(): string {
    const baseUrl = import.meta.env.VITE_OBSTACLES_CDN_BASEURL || import.meta.env.VITE_JETSTREAM_CDN_BASEURL
    return `${baseUrl}/bra/obstacles.geoz`
  }

  private async fetchRemoteBlob(): Promise<Blob | null> {
    try {
      const response = await this.httpClient.get<ArrayBuffer>(this.getRemoteUrl(), {
        timeout: HTTP_TIMEOUTS.dataset,
        responseType: 'arraybuffer'
      })

      if (!response.success || response.status < 200 || response.status >= 300) {
        this.logger.warn('ObstacleRepository', `Could not fetch obstacle artifact: ${describeHttpFailure(response)}`)
        return null
      }

      return this.extractDocGeojson(response.response)
    } catch (error: any) {
      this.logger.error('ObstacleRepository', `Could not fetch obstacle artifact: ${error.message}`)
      return null
    }
  }

  private async extractDocGeojson(arrayBuffer: ArrayBuffer): Promise<Blob | null> {
    try {
      const zip = await unzip(arrayBuffer)
      const docGeojson = zip.entries[ObstacleRepository.DOC_GEOJSON_ENTRY]

      if (!docGeojson) {
        this.logger.warn('ObstacleRepository', 'Missing doc.geojson in obstacle artifact')
        return null
      }

      const docGeojsonBuffer = await docGeojson.arrayBuffer()
      return new Blob([docGeojsonBuffer], { type: 'application/geo+json' })
    } catch (error: any) {
      this.logger.warn('ObstacleRepository', `Could not extract obstacle artifact: ${error.message}`)
      return null
    }
  }

  private async getCachedBlob(): Promise<Blob | null> {
    if (!this.isCacheStorageAvailable()) {
      return null
    }

    try {
      const cache = await caches.open(ObstacleRepository.CACHE_NAME)
      const response = await cache.match(ObstacleRepository.CACHE_URL)

      if (!response) {
        return null
      }

      const createdAt = Number(response.headers.get(ObstacleRepository.CACHE_CREATED_AT_HEADER))
      const isFresh = Number.isFinite(createdAt) && Date.now() - createdAt <= ObstacleRepository.CACHE_MAX_AGE_MS

      if (!isFresh) {
        await cache.delete(ObstacleRepository.CACHE_URL)
        return null
      }

      return response.blob()
    } catch (error: any) {
      this.logger.warn('ObstacleRepository', `Could not read obstacle cache: ${error.message}`)
      return null
    }
  }

  private async cacheBlob(blob: Blob): Promise<void> {
    if (!this.isCacheStorageAvailable()) {
      return
    }

    try {
      const cache = await caches.open(ObstacleRepository.CACHE_NAME)
      await cache.put(
        ObstacleRepository.CACHE_URL,
        new Response(blob, {
          headers: {
            [ObstacleRepository.CACHE_CREATED_AT_HEADER]: Date.now().toString(),
            'content-type': 'application/geo+json'
          }
        })
      )
    } catch (error: any) {
      this.logger.warn('ObstacleRepository', `Could not write obstacle cache: ${error.message}`)
    }
  }

  private createUrl(blob: Blob): string {
    const url = URL.createObjectURL(blob)
    this.activeUrls.add(url)
    this.ready = true
    return url
  }

  private revokeUrl(url: string): void {
    if (!this.activeUrls.has(url)) {
      return
    }

    URL.revokeObjectURL(url)
    this.activeUrls.delete(url)
    this.ready = this.activeUrls.size > 0
  }

  private isCacheStorageAvailable(): boolean {
    return typeof caches !== 'undefined'
  }
}
